// File: src/components/SearchBarWithSheet.jsx

import React from "react";
import { createPortal } from "react-dom";
import {
  LuSearch,
  LuX,
  LuSparkles,
  LuArrowUpAZ,
  LuCalendar,
} from "react-icons/lu";

/**
 * SearchBarWithSheet (single-field)
 *
 * Props:
 *  - onSearch(q: string): void
 *  - defaultQuery?: string
 *  - showClear?: boolean
 *  - onClear?: () => void
 *  - placeholder?: string
 *  - onOpenBubbles?: () => void
 *  - onOpenSort?: () => void
 *  - onOpenCalendar?: () => void
 *  - isSortActive?: boolean
 *  - isDateActive?: boolean
 *  - isBubblesActive?: boolean
 */
export default function SearchBarWithSheet({
  onSearch,
  defaultQuery = "",
  showClear = true,
  onClear,
  placeholder = "Search gatherings...",
  onOpenBubbles,
  onOpenSort,
  onOpenCalendar,
  isSortActive = false,
  isDateActive = false,
  isBubblesActive = false,
}) {
  const [showSheet, setShowSheet] =
    React.useState(false);

  const [q, setQ] = React.useState(defaultQuery);

  const isSearching = Boolean(q?.trim());

  function handleSubmit(innerQ = q) {
    if (typeof onSearch === "function") {
      onSearch(innerQ.trim());
    }
  }

  React.useEffect(() => {
    if (!showSheet) return undefined;

    const previousOverflow =
      document.body.style.overflow;

    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow =
        previousOverflow;
    };
  }, [showSheet]);

  const handleKeyDown = (event) => {
    if (event.key !== "Enter") return;

    event.preventDefault();
    handleSubmit();
    setShowSheet(false);

    setTimeout(() => {
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }, 40);
  };

  const ActiveDot = ({ active, label }) =>
    active ? (
      <span
        aria-label={label}
        title={label}
        style={activeDot}
      />
    ) : null;

  const ICON_COLOR = "#111111";
  const ICON_SIZE = 16;

  const toolbarIcon = {
    width: ICON_SIZE,
    height: ICON_SIZE,
    color: ICON_COLOR,
    opacity: 0.86,
    flexShrink: 0,
    strokeWidth: 2,
  };

  return (
    <>
      <div style={barWrap}>
        <button
          type="button"
          style={barButton}
          onClick={() => setShowSheet(true)}
          aria-label="Open search"
        >
          <LuSearch
            style={barIcon}
            aria-hidden="true"
          />

          <span
            style={{
              ...barText,
              ...(isSearching
                ? searchedBarText
                : null),
            }}
          >
            {isSearching ? q : placeholder}
          </span>
        </button>

        {showClear && isSearching && (
          <button
            type="button"
            style={clearBtn}
            onClick={() => {
              setQ("");

              if (typeof onClear === "function") {
                onClear();
              }
            }}
            aria-label="Clear search"
            title="Clear search"
          >
            <LuX
              style={clearIcon}
              aria-hidden="true"
            />
          </button>
        )}

        <div style={rightGroup}>
          {typeof onOpenBubbles === "function" && (
            <button
              type="button"
              aria-label="Open filters"
              onClick={onOpenBubbles}
              style={{
                ...iconBtn,
                ...(isBubblesActive
                  ? iconBtnActive
                  : null),
              }}
              title="Filters"
            >
              <span
                style={floatingDivider}
                aria-hidden="true"
              />

              <LuSparkles style={toolbarIcon} />

              <ActiveDot
                active={isBubblesActive}
                label="Filters active"
              />
            </button>
          )}

          <button
            type="button"
            aria-label="Open sort"
            onClick={onOpenSort}
            style={{
              ...iconBtn,
              ...(isSortActive
                ? iconBtnActive
                : null),
            }}
            title="Sort"
          >
            <span
              style={floatingDivider}
              aria-hidden="true"
            />

            <LuArrowUpAZ style={toolbarIcon} />

            <ActiveDot
              active={isSortActive}
              label="Sort active"
            />
          </button>

          <button
            type="button"
            aria-label="Open calendar"
            onClick={onOpenCalendar}
            style={{
              ...iconBtn,
              ...(isDateActive
                ? iconBtnActive
                : null),
            }}
            title="Date"
          >
            <span
              style={floatingDivider}
              aria-hidden="true"
            />

            <LuCalendar style={toolbarIcon} />

            <ActiveDot
              active={isDateActive}
              label="Date filter active"
            />
          </button>
        </div>
      </div>

      {showSheet &&
        createPortal(
          <div
            style={sheetBackdrop}
            onClick={() => setShowSheet(false)}
          >
            <div
              style={sheetCard}
              onClick={(event) =>
                event.stopPropagation()
              }
              role="dialog"
              aria-modal="true"
              aria-label="Search"
            >
              <div style={sheetHeader}>
                <div
                  style={{
                    fontWeight: 800,
                    fontSize: 16,
                  }}
                >
                  Search
                </div>

                <button
                  type="button"
                  aria-label="Close"
                  style={sheetClose}
                  onClick={() =>
                    setShowSheet(false)
                  }
                >
                  <LuX
                    style={{
                      color: "#0b0b0b",
                      fontSize: 18,
                    }}
                  />
                </button>
              </div>

              <div style={sheetFieldBox}>
                <div style={sheetFieldRow}>
                  <LuSearch
                    style={sheetFieldIcon}
                    aria-hidden="true"
                  />

                  <input
                    style={sheetInput}
                    value={q}
                    onChange={(event) =>
                      setQ(event.target.value)
                    }
                    onKeyDown={handleKeyDown}
                    placeholder="What are you looking for?"
                    autoFocus
                  />
                </div>
              </div>

              <div style={sheetBottomBar}>
                <button
                  type="button"
                  style={sheetSearchBtn}
                  onClick={() => {
                    handleSubmit();
                    setShowSheet(false);

                    setTimeout(() => {
                      window.scrollTo({
                        top: 0,
                        behavior: "smooth",
                      });
                    }, 40);
                  }}
                >
                  <LuSearch style={{ marginRight: 8 }} />
                  Search
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  );
}

/* ───────── Visible toolbar ───────── */

const barWrap = {
  width: "100%",
  minWidth: 0,
  height: 38,
  display: "flex",
  alignItems: "stretch",
  gap: 0,
  margin: "8px 0 0",
  overflow: "hidden",
  boxSizing: "border-box",
  border: "1px solid #E2E2E2",
  borderRadius: 10,
  background: "#FFFFFF",
  fontFamily:
    "'Inter', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
};

const barButton = {
  flex: "1 1 0",
  minWidth: 0,
  display: "flex",
  alignItems: "center",
  gap: 8,
  height: "100%",
  padding: "0 12px",
  boxSizing: "border-box",
  border: "none",
  borderRadius: 0,
  background: "transparent",
  cursor: "pointer",
  textAlign: "left",
  lineHeight: 1.2,
  overflow: "hidden",
  WebkitTapHighlightColor: "transparent",
};

const rightGroup = {
  display: "inline-flex",
  alignItems: "stretch",
  flexShrink: 0,
  height: "100%",
};

const iconBtn = {
  position: "relative",
  width: 34,
  height: "100%",
  flexShrink: 0,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 0,
  border: "none",
  borderRadius: 0,
  background: "transparent",
  boxShadow: "none",
  cursor: "pointer",
  WebkitTapHighlightColor: "transparent",
};

const iconBtnActive = {
  background: "#FFFFFF",
};

const floatingDivider = {
  position: "absolute",
  top: 7,
  bottom: 7,
  left: 0,
  width: 1,
  borderRadius: 999,
  background: "rgba(17,17,17,0.1)",
};

const activeDot = {
  position: "absolute",
  top: 5,
  right: 5,
  width: 5,
  height: 5,
  borderRadius: 999,
  background: "#111",
  boxShadow: "0 0 0 2px #FFFFFF",
};

const barIcon = {
  width: 19,
  height: 19,
  flexShrink: 0,
  color: "rgba(17,17,17,0.62)",
  strokeWidth: 2,
};

const barText = {
  minWidth: 0,
  flex: 1,
  display: "block",
  overflow: "hidden",
  color: "rgba(17,17,17,0.62)",
  fontSize: 14,
  fontWeight: 500,
  lineHeight: "18px",
  whiteSpace: "nowrap",
  textOverflow: "ellipsis",
};

const searchedBarText = {
  color: "#111",
  fontWeight: 600,
};

const clearBtn = {
  width: 34,
  height: "100%",
  flexShrink: 0,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 0,
  border: "none",
  background: "transparent",
  color: "rgba(17,17,17,0.58)",
  cursor: "pointer",
  WebkitTapHighlightColor: "transparent",
};

const clearIcon = {
  width: 16,
  height: 16,
  strokeWidth: 2,
};

/* ───────── Search sheet — unchanged ───────── */

const sheetBackdrop = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.45)",
  zIndex: 2000,
  display: "flex",
  justifyContent: "center",
  alignItems: "stretch",
};

const sheetCard = {
  width: "100%",
  maxWidth: 560,
  background: "#fff",
  borderRadius: 0,
  height: "100vh",
  boxShadow: "0 0 0 rgba(0,0,0,0)",
  display: "flex",
  flexDirection: "column",
};

const sheetHeader = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  fontSize: 16,
  padding: "14px 14px 10px",
  borderBottom: "1px solid #eee",
};

const sheetClose = {
  border: "1px solid #e5e7eb",
  width: 34,
  height: 34,
  borderRadius: 8,
  background: "#fff",
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  color: "#0b0b0b",
  appearance: "none",
  WebkitAppearance: "none",
  WebkitTapHighlightColor: "transparent",
};

const sheetFieldBox = {
  border: "1px solid #e5e7eb",
  borderRadius: 12,
  overflow: "hidden",
  margin: "14px",
};

const sheetFieldRow = {
  display: "flex",
  alignItems: "center",
  padding: "12px 14px",
  background: "#fff",
};

const sheetFieldIcon = {
  fontSize: 18,
  marginRight: 10,
  color: "#0b0b0b",
  opacity: 0.8,
  flexShrink: 0,
};

const sheetInput = {
  flex: 1,
  minWidth: 0,
  border: "none",
  outline: "none",
  fontSize: 15,
  color: "#111",
};

const sheetBottomBar = {
  marginTop: "auto",
  padding: "12px 14px 16px",
  borderTop: "1px solid #eee",
  position: "sticky",
  bottom: 0,
  background: "#fff",
};

const sheetSearchBtn = {
  width: "100%",
  height: 40,
  padding: "8px 14px",
  borderRadius: 10,
  border: "1px solid #0b0b0b",
  background: "#0b0b0b",
  color: "#ffffff",
  fontSize: 13,
  fontWeight: 700,
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 8,
};
